// src/pages/RoadmapPage.tsx
import React from "react";
import { useParams, Link } from "react-router-dom";
import CheckIcon from "../components/common/CheckIcon";
import dayjs from "dayjs";
import { useGetRoadmapByTokenQuery } from "../app/api/roadmapApi";

const RoadmapPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const {
    data: response,
    isLoading,
    isError,
  } = useGetRoadmapByTokenQuery(token);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brandWhite">
        <div className="text-center py-24">
          <div className="animate-pulse text-brandNavy opacity-80">
            Loading roadmap...
          </div>
        </div>
      </div>
    );
  }

  if (isError || !response?.ok || !response?.roadmap) {
    return (
      <div className="min-h-screen bg-white text-brandNavy p-12">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl font-bold">Roadmap not found</h2>
          <p className="mt-3 text-mutedGray">
            We couldn't find that roadmap. Check your link or retake the quiz.
          </p>
          <div className="mt-6">
            <Link
              to="/skill-to-sales/quiz"
              className="inline-block bg-brandRed text-white px-5 py-3 rounded font-semibold"
            >
              Retake Quiz
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const roadmap = response.roadmap;

  // Extract data from the roadmap object structure
  const name = roadmap.name || roadmap.simulation?.generatedFor?.name || "";
  //const email = roadmap.email || roadmap.simulation?.generatedFor?.email || "";
  const simulation = roadmap.simulation || {};
  const expanded = roadmap.expanded || {};

  // Prefer expanded fields, fallback to simulation fields
  const immediateNextSteps =
    expanded.immediateNextSteps || simulation.immediateNextSteps || [];
  const sevenDayPlan = expanded.sevenDayPlan || simulation.sevenDayPlan || [];
  const pricingAdvice =
    expanded.pricingAdvice || simulation.pricingAdvice || {};
  const salesCopies = expanded.salesCopies || simulation.salesCopies || {};
  const expandedProductLadder =
    expanded.expandedProductLadder ||
    simulation.expandedProductLadder ||
    simulation.productLadder ||
    {};
  const faqs = expanded.faqs || simulation.faqs || [];
  const roadmapPhases = simulation.roadmapPhases || [];
  const strategy = simulation.strategy || {};
  const generatedAt =
    simulation.generatedAt || roadmap.createdAt || new Date().toISOString();
  const checklist = expanded.checklist || simulation.checklist || [];

  const formatCurrency = (n?: number) => {
    if (n === undefined || n === null) return "—";
    if (n >= 1000000) return `₦${(n / 1000000).toFixed(1)}M`;
    return `₦${n.toLocaleString()}`;
  };

  return (
    <div className="min-h-screen bg-white text-brandNavy">
      <header className="bg-brandNavy text-white py-12">
        <div className="max-w-6xl mx-auto px-6">
          <h1 className="text-3xl md:text-4xl font-extrabold leading-tight">
            {name ? `${name}'s` : "Your"} Million-Naira Roadmap
          </h1>
          <p className="mt-3 text-white/90 max-w-3xl">
            A practical, step-by-step plan generated from your quiz. We've
            combined market data and tailored tactics so you know exactly what
            to do this week, this month, and over the next 6 months.
          </p>

          <div className="mt-6 flex gap-3">
            <Link
              to="/skill-to-sales/quiz"
              className="bg-white text-brandNavy px-4 py-2 rounded font-semibold"
            >
              Retake Quiz
            </Link>
            <a
              href={process.env.REACT_APP_WEBSITE_URL || "/"}
              className="bg-transparent border border-white/30 px-4 py-2 rounded text-white"
            >
              Visit Home
            </a>
          </div>

          <div className="mt-4 text-xl text-white/80">
            Generated {dayjs(generatedAt).format("MMM D, YYYY HH:mm")}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12 space-y-10">
        {/* Snapshot */}
        <section className="grid md:grid-cols-3 gap-6">
          <div
            className="bg-white p-6 rounded-2xl border"
            style={{ borderColor: "#E9EDF3" }}
          >
            <div className="text-xl text-mutedGray">Current Potential</div>
            <div className="text-2xl font-bold">
              {formatCurrency(simulation.currentPotential)}
            </div>
            <div className="text-xl text-mutedGray mt-2">estimated monthly</div>
          </div>

          <div
            className="bg-white p-6 rounded-2xl border"
            style={{ borderColor: "#E9EDF3" }}
          >
            <div className="text-xl text-mutedGray">Goal</div>
            <div className="text-2xl font-bold">
              {formatCurrency(simulation.monthlyGoal)}
            </div>
            <div className="text-xl text-mutedGray mt-2">target monthly</div>
          </div>

          <div
            className="bg-white p-6 rounded-2xl border"
            style={{ borderColor: "#E9EDF3" }}
          >
            <div className="text-xl text-mutedGray">Success Probability</div>
            <div className="text-2xl font-bold">
              {simulation.successProbability != null
                ? `${Math.round(Number(simulation.successProbability))}%`
                : "—"}
            </div>
          </div>
        </section>

        {/* Expanded Snapshot */}
        <section className="grid lg:grid-cols-3 gap-6">
          <div className="bg-brandLightNavy/5 p-6 rounded-2xl">
            <div className="font-semibold text-brandNavy">
              Quick Wins (72 hrs)
            </div>
            <ul className="mt-3 space-y-2 text-mutedGray">
              {immediateNextSteps.slice(0, 4).map((step: string, i: number) => (
                <li key={i} className="flex items-start gap-2">
                  <CheckIcon className="mt-1" />
                  <span>{step}</span>
                </li>
              ))}
            </ul>
          </div>

          <div
            className="bg-white p-6 rounded-2xl border"
            style={{ borderColor: "#E9EDF3" }}
          >
            <div className="font-semibold text-brandNavy">Primary Strategy</div>
            <div className="mt-2">
              <div
                className={`font-bold ${
                  strategy.color === "green"
                    ? "text-green-600"
                    : strategy.color === "yellow"
                    ? "text-yellow-600"
                    : strategy.color === "orange"
                    ? "text-orange-600"
                    : "text-red-600"
                }`}
              >
                {strategy.difficulty}
              </div>
              <div className="text-xl text-mutedGray mt-2">
                {strategy.focus}
              </div>
              <div className="text-xl text-mutedGray mt-1">
                Timeline: {strategy.timeline}
              </div>
            </div>
          </div>

          <div
            className="bg-white p-6 rounded-2xl border"
            style={{ borderColor: "#E9EDF3" }}
          >
            <div className="font-semibold text-brandNavy">Checklist</div>
            <ul className="mt-3 space-y-2 text-mutedGray text-xl">
              {checklist.slice(0, 4).map((c: string, i: number) => (
                <li key={i} className="flex items-start gap-2">
                  <CheckIcon className="mt-1 flex-shrink-0" />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Product Ladder (expanded) */}
        <section>
          <h2 className="text-xl font-semibold text-brandNavy mb-4">
            Your Expanded 3-Phase Product Ladder
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {Object.entries(expandedProductLadder).map(
              ([k, p]: [string, any]) => (
                <div
                  key={k}
                  className="bg-white p-6 rounded-2xl border"
                  style={{ borderColor: "#E9EDF3" }}
                >
                  <div>
                    <div className="font-semibold text-xl">{p.name}</div>
                    <div className="text-brandRed font-bold mt-1">
                      {p.price}
                    </div>
                    <div className="text-xl text-mutedGray mt-2">
                      {p.description}
                    </div>
                    <div className="mt-3 text-xl bg-brandLightNavy/5 p-2 rounded">
                      {p.timeline} • Target: {p.targetSales}
                    </div>
                  </div>

                  <div className="mt-4">
                    <div className="font-semibold text-xl text-brandNavy mb-2">
                      Tactical Steps
                    </div>
                    <ul className="list-inside list-disc text-xl text-mutedGray space-y-1">
                      {(p.tacticalSteps || []).map((t: string, i: number) => (
                        <li key={i}>{t}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="mt-4">
                    <div className="font-semibold text-xl text-brandNavy mb-2">
                      KPIs
                    </div>
                    <div className="text-xl text-mutedGray">
                      {(p.kpis || []).join(" • ") ||
                        "Conversion, AOV, Refund rate"}
                    </div>
                  </div>
                </div>
              )
            )}
          </div>
        </section>

        {/* Month-by-Month Roadmap */}
        <section>
          <h2 className="text-xl font-semibold text-brandNavy mb-4">
            Month-by-Month Roadmap
          </h2>
          <div className="space-y-4">
            {roadmapPhases.map((phase: any) => (
              <div
                key={phase.phase}
                className="bg-white p-6 rounded-2xl border"
                style={{ borderColor: "#E9EDF3" }}
              >
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="font-bold text-xl">{phase.title}</div>
                    <div className="text-xl text-mutedGray">
                      {phase.timeline}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-brandNavy">
                      {formatCurrency(phase.revenue)}
                    </div>
                    <div className="text-xl text-mutedGray">projected</div>
                  </div>
                </div>

                <div className="mb-3">
                  <div className="font-semibold text-xl text-brandNavy mb-2">
                    Key Actions
                  </div>
                  <ul className="list-inside list-disc text-mutedGray space-y-1 text-xl">
                    {(phase.keyActions || []).map((a: string, i: number) => (
                      <li key={i}>{a}</li>
                    ))}
                  </ul>
                </div>

                <div>
                  <div className="font-semibold text-xl text-brandNavy mb-2">
                    Milestones
                  </div>
                  <div className="text-xl text-mutedGray">
                    {(phase.milestones || []).join(" • ")}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* 7-Day Plan */}
        <section>
          <h2 className="text-xl font-semibold text-brandNavy mb-4">
            7-Day Sprint
          </h2>
          <div className="grid md:grid-cols-7 gap-3">
            {sevenDayPlan.map((d: any) => (
              <div
                key={d.day}
                className="bg-white p-4 rounded-md border text-center"
                style={{ borderColor: "#E9EDF3" }}
              >
                <div className="text-xs text-mutedGray">Day {d.day}</div>
                <div className="mt-2 font-semibold text-xl">{d.task}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Pricing & Copy */}
        <section className="grid md:grid-cols-2 gap-6">
          <div
            className="bg-white p-6 rounded-2xl border"
            style={{ borderColor: "#E9EDF3" }}
          >
            <h3 className="font-semibold mb-3">Pricing Advice</h3>
            <div className="text-mutedGray text-xl space-y-2">
              <div>
                <strong>Starter:</strong> {pricingAdvice.starter}
              </div>
              <div>
                <strong>Growth:</strong> {pricingAdvice.growth}
              </div>
              <div>
                <strong>Premium:</strong> {pricingAdvice.premium}
              </div>
              <div className="mt-3 text-xl text-mutedGray italic">
                {pricingAdvice.note}
              </div>
            </div>
          </div>

          <div
            className="bg-white p-6 rounded-2xl border"
            style={{ borderColor: "#E9EDF3" }}
          >
            <h3 className="font-semibold mb-3">Ready-to-use Messaging</h3>
            <div className="text-mutedGray space-y-3 text-xl">
              <div>
                <div className="font-semibold text-brandNavy">
                  WhatsApp Pitch
                </div>
                <div className="mt-1">{salesCopies.whatsappPitch}</div>
              </div>
              <div>
                <div className="font-semibold text-brandNavy">Email Pitch</div>
                <div className="mt-1 whitespace-pre-line">
                  {salesCopies.emailPitch}
                </div>
              </div>
              <div>
                <div className="font-semibold text-brandNavy">
                  Product Pitch
                </div>
                <div className="mt-1">{salesCopies.productPitch}</div>
              </div>
            </div>
          </div>
        </section>

        {/* Next Step CTA & FAQ */}
        <section className="grid md:grid-cols-2 gap-6">
          {/* Next Step CTA */}
          <div className="bg-gradient-to-br from-brandNavy to-brandLightNavy text-white p-8 rounded-2xl flex flex-col justify-center items-center text-center">
            <div className="text-4xl mb-4">🚀</div>
            <h3 className="font-bold text-2xl mb-3">Ready to Take Action?</h3>
            <p className="text-white/90 mb-6 text-base">
              You've seen your roadmap. Now learn the exact system to turn this
              plan into reality.
            </p>
            <Link
              to="/dbc/module-1"
              className="inline-block bg-brandRed text-white px-8 py-4 rounded-lg font-bold text-xl hover:bg-red-700 transition-all transform hover:scale-105 shadow-xl"
            >
              Join FREE Module 1 Session →
            </Link>
            <p className="text-white/70 text-xl mt-4">
              Limited spots available • 100% Free
            </p>
          </div>

          {/* FAQ */}
          <div
            className="bg-white p-6 rounded-2xl border"
            style={{ borderColor: "#E9EDF3" }}
          >
            <h3 className="font-semibold mb-3">Frequently Asked</h3>
            <div className="space-y-3 text-xl text-mutedGray">
              {faqs.map((f: any, i: number) => (
                <div key={i}>
                  <div className="font-semibold text-brandNavy">{f.q}</div>
                  <div className="mt-1">{f.a}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default RoadmapPage;
